import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  Inject,
  ChangeDetectorRef,
  Optional,
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AudioUnlockService } from 'app/core/services/audio/audio-unlock.service';
import { CallOverlayService } from 'app/core/services/call-overlay/call-overlay.service';
import {
  CallHubService,
  IncomingCallEvent,
  VideoStatusEvent,
  CallErrorEvent,
} from 'app/core/services/chat/call-hub.service';
import { ToastService } from 'app/core/services/toast.service';
import { Subject, takeUntil, take, Subscription } from 'rxjs';

@Component({
  selector: 'app-video-call-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './video-call-modal.component.html',
  styleUrls: ['./video-call-modal.component.scss'],
})
export class VideoCallModalComponent implements OnInit, OnDestroy {
  @Input() incomingCall?: IncomingCallEvent;
  @Input() calleeUserId?: string;
  @Input() patientId?: string;
  @Input() callType: any = 'Video';
  @Input() ringtoneAudio?: HTMLAudioElement;
  @Input() callContextType: any = 'Patient';

  @ViewChild('localVideo', { static: false })
  localVideo?: ElementRef<HTMLVideoElement>;
  @ViewChild('remoteVideo', { static: false })
  remoteVideo?: ElementRef<HTMLVideoElement>;
  isRemoteVideoOff = false;
  private currentRemoteStream: MediaStream | null = null;
  @ViewChild('minRemoteVideo', { static: false })
  minRemoteVideo?: ElementRef<HTMLVideoElement>;

  inPrompt = false;
  inCall = false;
  isMuted = false;
  isVideoOff = false;
  isMinimized = false;
  isMinHovered = false;

  minimizedLeft = 12;
  minimizedBottom = 12;
  minimizedWidth = 220;
  minimizedHeight = 140;

  private dragActive = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  private resizeActive = false;
  private resizeEdge = '';
  private resizeStartX = 0;
  private resizeStartY = 0;
  private resizeStartWidth = 0;
  private resizeStartHeight = 0;
  private resizeStartLeft = 0;
  private resizeStartBottom = 0;

  private dragMoved = false;
  private readonly DRAG_THRESHOLD = 6;

  callStartTime: number | null = null;
  duration = '00:00';
  private durationTimer?: any;

  private currentCallId?: string;
  private endTonePlayed = false;

  private subs: Subscription[] = [];

  private onKeyDown = (ev: KeyboardEvent) => {
    try {
      if (ev.key !== 'Escape') return;
      if (ev.repeat) return;
      if (this.inPrompt || !this.inCall) return;
      if (this.isMinimized) return;
      const active = document.activeElement as HTMLElement | null;
      if (active) {
        const tag = (active.tagName || '').toUpperCase();
        if (tag === 'INPUT' || tag === 'TEXTAREA' || active.isContentEditable)
          return;
      }
      if (ev.altKey || ev.ctrlKey || ev.metaKey || ev.shiftKey) return;

      try {
        this.toggleMinimize();
      } catch {}
      ev.preventDefault();
    } catch {}
  };

  private lastRemoteStream: MediaStream | null = null;
  private lastLocalStream: MediaStream | null = null;

  minPlaybackBlocked = false;

  private _remoteMetadataHandler?: (this: HTMLVideoElement, ev: Event) => void;
  private _minRemoteMetadataHandler?: (
    this: HTMLVideoElement,
    ev: Event
  ) => void;

  constructor(
    @Inject(CallHubService) private vc: CallHubService,
    private toastService: ToastService,
    private translate: TranslateService,
    private audioUnlock: AudioUnlockService,
    private cdr: ChangeDetectorRef,
    private overlay: CallOverlayService,
    @Inject(NgbActiveModal) @Optional() public activeModal?: NgbActiveModal
  ) {}

  get isVideoCall(): boolean {
    return this.isVideoCallType(this.incomingCall?.callType ?? this.callType);
  }

  get callerLabel(): string {
    const ic: any = this.incomingCall || {};
    return (
      ic.fromUserName ||
      ic.fromUserId ||
      this.translate.instant('video-call.unknown-user')
    );
  }

  get callerInitials(): string {
    const name = (this.callerLabel || '').trim();
    if (!name) return 'U';
    const parts = name.split(/\s+/).filter(Boolean);
    const initials =
      (parts[0]?.[0] || '') + (parts.length > 1 ? parts[1]?.[0] || '' : '');
    return initials.toUpperCase() || 'U';
  }

  get callTypeLabel(): string {
    return this.translate.instant(
      this.isVideoCall
        ? 'video-call.wants-video-call'
        : 'video-call.wants-audio-call'
    );
  }

  get muteIcon(): string {
    return this.isMuted
      ? '/assets/icons/mic-off.svg'
      : '/assets/icons/mic-on.svg';
  }

  get cameraIcon(): string {
    return this.isVideoOff
      ? '/assets/icons/camera-off.svg'
      : '/assets/icons/camera-on.svg';
  }

  get hangupIcon(): string {
    return '/assets/icons/phone-off.svg';
  }

  private isVideoCallType(ct: any): boolean {
    if (typeof ct === 'number') return ct === 2;
    const s = String(ct || '').toLowerCase();
    return s === 'video';
  }

  ngOnInit(): void {
    this.vc.ensureSignaling().catch(() => {});

    try {
      this.overlay.maximize();
      this.isMinimized = false;
    } catch {}

    if (this.incomingCall) {
      this.inPrompt = true;
      this.currentCallId = this.incomingCall.callId;
    }

    try {
      const sm = this.overlay.getMinimized$().subscribe((v) => {
        if (this.inPrompt) return;
        this.isMinimized = !!v;
        try {
          this.cdr.detectChanges();
        } catch {}
      });
      this.subs.push(sm);
    } catch {}

    if (!this.incomingCall && (this.calleeUserId || this.patientId)) {
      this.startOutgoing();
    }

    const s = this.vc.onCallEnded().subscribe((ev) => {
      if (!ev) return;
      if (
        this.currentCallId &&
        ev.callId &&
        ev.callId === this.currentCallId
      ) {
        this.stopRingtone();
        this.playEndToneOnce();
        this.inCall = false;
        this.stopDurationTimer();
        try {
          if (this.activeModal) this.activeModal.close('ended');
          else this.overlay.end();
        } catch {}
      }
    });
    this.subs.push(s);

    const rj = this.vc.onCallRejected().subscribe((ev) => {
      if (!ev) return;
      if (
        this.currentCallId &&
        ev.callId &&
        ev.callId === this.currentCallId
      ) {
        this.stopRingtone();
        this.notifyRejection();
        try {
          if (this.activeModal) this.activeModal.close('rejected');
          else this.overlay.end();
        } catch {}
      }
    });
    this.subs.push(rj);

    document.addEventListener('keydown', this.onKeyDown);

    const s2 = this.vc.getRemoteStream$().subscribe((s) => {
      this.currentRemoteStream = s;
      if (this.remoteVideo?.nativeElement) {
        if (!this.isRemoteVideoOff) {
          this.remoteVideo.nativeElement.srcObject = s;
          if (s && this.remoteVideo.nativeElement.paused)
            this.remoteVideo.nativeElement.play().catch(() => {});
        }
      }
    });
    this.subs.push(s2);

    const statusSub = this.vc
      .onVideoStatusChanged()
      .subscribe((evt: VideoStatusEvent) => {
        if (evt.callId === this.currentCallId) {
          const isFromRemote = this.incomingCall 
            ? evt.fromUserId === this.incomingCall.fromUserId
            : evt.fromUserId === this.calleeUserId;
            
          if (isFromRemote) {
            this.isRemoteVideoOff = evt.isVideoOff;
            this.updateRemoteVideoVisibility();
          }
        }
      });
    this.subs.push(statusSub);

    const errorSub = this.vc.onCallError().subscribe((err: CallErrorEvent) => {
      this.handleCallError(err);
    });
    this.subs.push(errorSub);
  }

  private handleCallError(error: CallErrorEvent): void {
    const errorCode = error.errorCode;
    let messageKey: string;

    switch (errorCode) {
      case 'CALL_ALREADY_IN_ACTIVE_CALL':
        messageKey = 'CallErrors.AlreadyInActiveCall';
        break;
      case 'CALL_TARGET_USER_BUSY':
        messageKey = 'CallErrors.TargetUserBusy';
        break;
      case 'CALL_TARGET_REQUIRED':
        messageKey = 'CallErrors.TargetRequired';
        break;
      case 'CALL_INVALID_CALL':
        messageKey = 'CallErrors.InvalidCall';
        break;
      default:
        messageKey = 'CallErrors.UnknownError';
        break;
    }

    const message = this.translate.instant(messageKey);
    this.toastService.showToast(message, 'error');

    this.stopRingtone();
    
    if (this.currentCallId) {
      this.vc.endCall().catch(() => {});
    }
    
    if (this.activeModal) this.activeModal.dismiss('call-error');
    else this.overlay.end();
  }

  private updateRemoteVideoVisibility() {
    if (!this.remoteVideo?.nativeElement) return;

    if (this.isRemoteVideoOff) {
      this.remoteVideo.nativeElement.srcObject = null;
    } else {
      if (this.currentRemoteStream) {
        this.remoteVideo.nativeElement.srcObject = this.currentRemoteStream;
        this.remoteVideo.nativeElement.play().catch(() => {});
      }
    }
  }

  async accept() {
    if (!this.incomingCall) return;
    this.inPrompt = false;
    try {
      const video = this.isVideoCallType(
        this.incomingCall.callType ?? this.callType
      );
      await this.vc.acceptIncomingCall(this.incomingCall, {
        video,
        audio: true,
      });

      this.currentCallId = this.incomingCall.callId;
      this.bindStreams();
      this.inCall = true;
      this.startDurationTimer();

      setTimeout(() => this.stopRingtone(), 150);
    } catch (e: any) {
      console.error('[VideoCallModal] accept() failed:', e);
      this.stopRingtone();
      
      let errorMessage = this.translate.instant('video-call.accept-failed');
      
      if (e?.message === 'MEDIA_PERMISSION_DENIED') {
        errorMessage = this.translate.instant('CallErrors.MediaPermissionDenied');
      } else if (e?.message === 'MEDIA_DEVICE_NOT_FOUND') {
        errorMessage = this.translate.instant('CallErrors.MediaDeviceNotFound');
      } else if (e?.message === 'MEDIA_DEVICE_IN_USE') {
        errorMessage = this.translate.instant('CallErrors.MediaDeviceInUse');
      } else if (e?.message === 'CALL_ALREADY_IN_ACTIVE_CALL') {
        errorMessage = this.translate.instant('CallErrors.AlreadyInActiveCall');
      }
      
      this.toastService.showToast(errorMessage, 'error');
      
      if (this.incomingCall?.callId) {
        try {
          await this.vc.rejectIncomingCall(this.incomingCall.callId, 'DeviceError');
        } catch (rejectError) {
          console.error('[VideoCallModal] Failed to reject call:', rejectError);
        }
      }
      
      if (this.activeModal) {
        this.activeModal.dismiss('accept-failed');
      } else {
        this.overlay.end();
      }
    }
  }

  async reject() {
    this.stopRingtone();
    if (!this.incomingCall) return this.activeModal?.dismiss('rejected');
    try {
      await this.vc.rejectIncomingCall(this.incomingCall.callId);
      try {
        if (this.activeModal) this.activeModal.close('rejected');
        else this.overlay.end();
      } catch {}
    } catch {
      if (this.activeModal) this.activeModal.dismiss('reject-failed');
      else this.overlay.end();
    }
  }

  async hangup() {
    this.playEndToneOnce();
    await this.vc.endCall();
    this.inCall = false;
    this.stopDurationTimer();
    try {
      if (this.activeModal) this.activeModal.close('ended');
      else this.overlay.end();
    } catch {}
  }

  async toggleMute() {
    this.isMuted = !this.isMuted;
    this.vc.muteAudio(this.isMuted);
  }

  async toggleVideo() {
    this.isVideoOff = !this.isVideoOff;
    this.vc.disableVideo(this.isVideoOff);
  }

  toggleMinimize() {
    if (!this.inCall) return;
    this.isMinimized = !this.isMinimized;

    try {
      if (this.isMinimized) this.overlay.minimize();
      else this.overlay.maximize();
    } catch {}

    try {
      if (this.activeModal) {
        if (this.isMinimized) {
          document.body.classList.add('vc-modal-minimized');
        } else {
          document.body.classList.remove('vc-modal-minimized');
        }
      }
    } catch {}

    try {
      if (this.isMinimized) {
        if (this.lastRemoteStream && this.minRemoteVideo?.nativeElement) {
          const el = this.minRemoteVideo.nativeElement;
          el.muted = false;
          el.srcObject = this.lastRemoteStream;

          if (this._minRemoteMetadataHandler) {
            el.removeEventListener(
              'loadedmetadata',
              this._minRemoteMetadataHandler
            );
            this._minRemoteMetadataHandler = undefined;
          }
          this._minRemoteMetadataHandler = () => {
            this.adjustRemoteFit(el, true);
          };
          el.addEventListener('loadedmetadata', this._minRemoteMetadataHandler);

          el.play()
            .then(() => {
              this.minPlaybackBlocked = false;
              try {
                this.cdr.detectChanges();
              } catch {}
            })
            .catch(() => {
              this.minPlaybackBlocked = true;
              try {
                this.cdr.detectChanges();
              } catch {}
            });
        }
      } else {
        try {
          this.cdr.detectChanges();
        } catch {}
        Promise.resolve().then(() => {
          try {
            if (this.lastRemoteStream && this.remoteVideo?.nativeElement) {
              const el = this.remoteVideo.nativeElement;
              el.muted = false;
              el.srcObject = this.lastRemoteStream;

              if (this._remoteMetadataHandler) {
                el.removeEventListener(
                  'loadedmetadata',
                  this._remoteMetadataHandler
                );
                this._remoteMetadataHandler = undefined;
              }
              this._remoteMetadataHandler = () => {
                this.adjustRemoteFit(el, false);
              };
              el.addEventListener(
                'loadedmetadata',
                this._remoteMetadataHandler
              );

              el.play().catch(() => {});
            }
            if (this.lastLocalStream && this.localVideo?.nativeElement) {
              try {
                const lel = this.localVideo.nativeElement;
                lel.muted = true;
                lel.srcObject = this.lastLocalStream;
                lel.play().catch(() => {});
              } catch {}
            }
          } catch {}
        });
      }
    } catch {}
    try {
      this.cdr.detectChanges();
    } catch {}
  }

  async resumeMinPlayback() {
    try {
      await this.audioUnlock.waitUntilUnlocked(2000).catch(() => {});
    } catch {}

    try {
      if (this.minRemoteVideo?.nativeElement && this.lastRemoteStream) {
        const el = this.minRemoteVideo.nativeElement;
        el.muted = false;
        el.srcObject = this.lastRemoteStream;
        await el.play();
        this.minPlaybackBlocked = false;
        try {
          this.cdr.detectChanges();
        } catch {}
      }
    } catch {
      this.minPlaybackBlocked = true;
      try {
        this.cdr.detectChanges();
      } catch {}
    }
  }

  private async startOutgoing() {
    try {
      const video = this.isVideoCallType(this.callType);
      const { callId } = await this.vc.beginOutgoingCall({
        calleeUserId: this.calleeUserId,
        patientId: this.patientId,
        callType: this.callType,
        video,
        audio: true,
        callContextType: this.callContextType,
      });

      this.currentCallId = callId;
      this.bindStreams();
      this.inCall = true;
      this.startDurationTimer();
    } catch (e: any) {
      console.error('[VideoCallModal] startOutgoing() failed:', e);
      
      let errorMessage = this.translate.instant('video-call.start-failed');
      
      if (e?.message === 'MEDIA_PERMISSION_DENIED') {
        errorMessage = this.translate.instant('CallErrors.MediaPermissionDenied');
      } else if (e?.message === 'MEDIA_DEVICE_NOT_FOUND') {
        errorMessage = this.translate.instant('CallErrors.MediaDeviceNotFound');
      } else if (e?.message === 'MEDIA_DEVICE_IN_USE') {
        errorMessage = this.translate.instant('CallErrors.MediaDeviceInUse');
      } else if (e?.message === 'CALL_ALREADY_IN_ACTIVE_CALL') {
        errorMessage = this.translate.instant('CallErrors.AlreadyInActiveCall');
      }
      
      this.toastService.showToast(errorMessage, 'error');
      
      this.activeModal?.dismiss('start-failed');
    }
  }

  private bindStreams() {
    const s1 = this.vc.getLocalStream$().subscribe((s) => {
      this.lastLocalStream = s || null;

      if (this.localVideo?.nativeElement) {
        try {
          const el = this.localVideo.nativeElement;
          el.muted = true;
          el.srcObject = s;
          if (s && el.paused) el.play().catch(() => {});
        } catch {}
      }
    });
    const s2 = this.vc.getRemoteStream$().subscribe((s) => {
      this.lastRemoteStream = s || null;

      if (this.remoteVideo?.nativeElement) {
        try {
          const el = this.remoteVideo.nativeElement;
          el.muted = false;
          el.srcObject = s;

          if (this._remoteMetadataHandler) {
            el.removeEventListener(
              'loadedmetadata',
              this._remoteMetadataHandler
            );
            this._remoteMetadataHandler = undefined;
          }
          this._remoteMetadataHandler = () => {
            this.adjustRemoteFit(el, false);
          };
          el.addEventListener('loadedmetadata', this._remoteMetadataHandler);

          if (s && el.paused) el.play().catch(() => {});
        } catch {}
      }

      if (this.minRemoteVideo?.nativeElement) {
        try {
          const el = this.minRemoteVideo.nativeElement;
          el.muted = false;
          el.srcObject = s;

          if (this._minRemoteMetadataHandler) {
            el.removeEventListener(
              'loadedmetadata',
              this._minRemoteMetadataHandler
            );
            this._minRemoteMetadataHandler = undefined;
          }
          this._minRemoteMetadataHandler = () => {
            this.adjustRemoteFit(el, true);
          };
          el.addEventListener('loadedmetadata', this._minRemoteMetadataHandler);

          if (s) {
            el.play()
              .then(() => {
                this.minPlaybackBlocked = false;
                try {
                  this.cdr.detectChanges();
                } catch {}
              })
              .catch(() => {
                this.minPlaybackBlocked = true;
                try {
                  this.cdr.detectChanges();
                } catch {}
              });
          }
        } catch {
          this.minPlaybackBlocked = true;
          try {
            this.cdr.detectChanges();
          } catch {}
        }
      }
    });
    this.subs.push(s1, s2);
  }

  private adjustRemoteFit(el: HTMLVideoElement, isMinimized: boolean) {
    try {
      const vw = el.videoWidth || 0;
      const vh = el.videoHeight || 0;
      if (!vw || !vh) return;

      const container = el.closest('.videos') as HTMLElement | null;
      const contRect = container
        ? container.getBoundingClientRect()
        : el.parentElement
        ? el.parentElement.getBoundingClientRect()
        : null;
      if (!contRect) return;

      const containerW = Math.max(1, contRect.width);
      const containerH = Math.max(1, contRect.height);

      const scaleFill = Math.max(containerW / vw, containerH / vh);
      const UPSCALE_THRESHOLD = 1.1;

      if (isMinimized) {
        el.style.objectFit = 'contain';
        el.style.objectPosition = 'center';
        el.style.position = 'absolute';
        el.style.inset = '0';
        el.style.width = '100%';
        el.style.height = '100%';
      } else {
        if (scaleFill > UPSCALE_THRESHOLD) {
          el.style.objectFit = 'contain';
          el.style.objectPosition = 'center';
        } else {
          el.style.objectFit = 'cover';
          el.style.objectPosition = 'center';
        }
        el.style.position = 'absolute';
        el.style.inset = '0';
        el.style.width = '100%';
        el.style.height = '100%';
      }
    } catch {}
  }

  ngOnDestroy(): void {
    this.stopRingtone();
    this.subs.forEach((s) => s.unsubscribe());
    this.stopDurationTimer();
    this.removeDragListeners();

    document.removeEventListener('keydown', this.onKeyDown);

    try {
      document.body.classList.remove('vc-modal-minimized');
    } catch {}

    try {
      if (this.overlay.getActiveValue && this.overlay.getActiveValue()) {
        this.overlay.end();
      }
    } catch {}

    try {
      if (this.remoteVideo?.nativeElement && this._remoteMetadataHandler) {
        this.remoteVideo.nativeElement.removeEventListener(
          'loadedmetadata',
          this._remoteMetadataHandler
        );
        this._remoteMetadataHandler = undefined;
      }
    } catch {}
    try {
      if (
        this.minRemoteVideo?.nativeElement &&
        this._minRemoteMetadataHandler
      ) {
        this.minRemoteVideo.nativeElement.removeEventListener(
          'loadedmetadata',
          this._minRemoteMetadataHandler
        );
        this._minRemoteMetadataHandler = undefined;
      }
    } catch {}
    
    try {
      this.vc.getCallState().pipe(take(1)).subscribe(async (state) => {
        if (state.inCall && this.currentCallId) {
          try {
            await this.vc.endCall();
          } catch (err) {
            console.error('[VideoCallModal] Failed to end call on destroy:', err);
          }
        }
      });
    } catch (err) {
      console.error('[VideoCallModal] Error checking call state on destroy:', err);
    }
    
    try {
      this.vc.getLocalStream$().pipe(take(1)).subscribe(stream => {
        stream?.getTracks().forEach(track => {
          try {
            track.stop();
          } catch {}
        });
      });
    } catch {}
  }

  private stopRingtone(): void {
    try {
      this.ringtoneAudio?.pause();
      if (this.ringtoneAudio) this.ringtoneAudio.currentTime = 0;
      this.ringtoneAudio = undefined;
    } catch {}
  }

  private playEndToneOnce(): void {
    console.log('playEndToneOnce called');
    if (this.endTonePlayed) return;
    this.endTonePlayed = true;
    this.playEndTone().catch(() => {});
  }

  private async playEndTone(): Promise<void> {
    await this.audioUnlock.waitUntilUnlocked(2000).catch(() => {});

    try {
      const audio = new Audio('/assets/sounds/call-ended.mp3');
      audio.volume = 0.6;
      await audio.play();
      return;
    } catch {}

    try {
      const AudioCtx =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = 600;
      o.connect(g);
      g.connect(ctx.destination);

      const now = ctx.currentTime;
      g.gain.setValueAtTime(0.0001, now);
      g.gain.exponentialRampToValueAtTime(0.15, now + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);

      o.start(now);
      o.stop(now + 0.3);

      setTimeout(() => {
        try {
          ctx.close();
        } catch {}
      }, 400);
    } catch {}
  }

  private notifyRejection(): void {
    try {
      this.toastService.showToast(
        this.translate.instant('video-call.call-was-not-answered-or-rejected'),
        'warning'
      );
    } catch {}
  }

  private startDurationTimer(): void {
    this.callStartTime = Date.now();
    this.updateDuration();
    this.durationTimer = setInterval(() => this.updateDuration(), 1000);
  }

  private stopDurationTimer(): void {
    if (this.durationTimer) {
      clearInterval(this.durationTimer);
      this.durationTimer = undefined;
    }
    this.callStartTime = null;
  }

  private updateDuration(): void {
    if (!this.callStartTime) {
      this.duration = '00:00';
      return;
    }
    const sec = Math.floor((Date.now() - this.callStartTime) / 1000);
    const hh = Math.floor(sec / 3600);
    const mm = Math.floor((sec % 3600) / 60);
    const ss = sec % 60;
    this.duration =
      (hh > 0 ? String(hh).padStart(2, '0') + ':' : '') +
      String(mm).padStart(2, '0') +
      ':' +
      String(ss).padStart(2, '0');
  }

  onMinHover(hover: boolean) {
    this.isMinHovered = hover;
  }

  onMinMouseDown(evt: MouseEvent) {
    try {
      const target = evt.target as HTMLElement | null;
      if (
        target &&
        target.closest &&
        target.closest('button, a, [data-no-drag], .btn')
      ) {
        return;
      }
    } catch {}

    if (evt.button !== 0) return;
    const el = (evt.currentTarget as HTMLElement).closest(
      '.minimized'
    ) as HTMLElement;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    this.dragActive = true;
    this.dragMoved = false;
    this.dragOffsetX = evt.clientX - rect.left;
    this.dragOffsetY = evt.clientY - rect.top;
    document.addEventListener('mousemove', this.onDocMouseMove);
    document.addEventListener('mouseup', this.onDocMouseUp);
    evt.preventDefault();
  }

  onMinTouchStart(evt: TouchEvent) {
    try {
      const t = evt.touches && evt.touches[0];
      if (!t) return;
      const target = (evt.target as HTMLElement) || null;
      if (
        target &&
        target.closest &&
        target.closest('button, a, [data-no-drag], .btn')
      ) {
        return;
      }
      const el = (evt.currentTarget as HTMLElement).closest(
        '.minimized'
      ) as HTMLElement;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      this.dragActive = true;
      this.dragMoved = false;
      this.dragOffsetX = t.clientX - rect.left;
      this.dragOffsetY = t.clientY - rect.top;

      document.addEventListener('touchmove', this.onDocTouchMove as any, {
        passive: false,
      });
      document.addEventListener('touchend', this.onDocTouchEnd as any);
      evt.preventDefault();
    } catch {}
  }

  private onDocTouchMove = (evt: TouchEvent) => {
    if (!this.dragActive) return;
    try {
      const t = evt.touches && evt.touches[0];
      if (!t) return;

      const width = this.minimizedWidth;
      const height = this.minimizedHeight;
      const newLeft = Math.max(
        8,
        Math.min(window.innerWidth - width - 8, t.clientX - this.dragOffsetX)
      );
      const newTop = Math.max(
        8,
        Math.min(window.innerHeight - height - 8, t.clientY - this.dragOffsetY)
      );

      try {
        const dx = Math.abs(
          t.clientX - (this.dragOffsetX + (this.minimizedLeft || 0))
        );
        const dy = Math.abs(
          t.clientY -
            (window.innerHeight -
              this.minimizedBottom -
              height +
              (this.dragOffsetY || 0))
        );
        if (dx > this.DRAG_THRESHOLD || dy > this.DRAG_THRESHOLD)
          this.dragMoved = true;
      } catch {}
      this.minimizedLeft = Math.round(newLeft);
      this.minimizedBottom = Math.round(window.innerHeight - newTop - height);
      evt.preventDefault();
    } catch {}
  };

  private onDocTouchEnd = (evt: TouchEvent) => {
    if (!this.dragActive) return;
    try {
      if (!this.dragMoved) {
        this.toggleMinimize();
      }
    } catch {}
    this.dragActive = false;
    this.removeDragListeners();
  };

  private onDocMouseMove = (evt: MouseEvent) => {
    if (this.resizeActive) {
      this.handleResize(evt);
      return;
    }
    if (!this.dragActive) return;
    try {
      const dx = Math.abs(
        evt.clientX - (this.dragOffsetX + (this.minimizedLeft || 0))
      );
      const dy = Math.abs(
        evt.clientY -
          (window.innerHeight -
            this.minimizedBottom -
            this.minimizedHeight +
            (this.dragOffsetY || 0))
      );
      if (dx > this.DRAG_THRESHOLD || dy > this.DRAG_THRESHOLD) {
        this.dragMoved = true;
      }
    } catch {}
    if (!this.dragActive) return;
    const width = this.minimizedWidth;
    const height = this.minimizedHeight;
    const newLeft = Math.max(
      8,
      Math.min(window.innerWidth - width - 8, evt.clientX - this.dragOffsetX)
    );
    const newTop = Math.max(
      8,
      Math.min(window.innerHeight - height - 8, evt.clientY - this.dragOffsetY)
    );
    this.minimizedLeft = Math.round(newLeft);
    this.minimizedBottom = Math.round(window.innerHeight - newTop - height);
  };

  private onDocMouseUp = (evt: MouseEvent) => {
    if (this.resizeActive) {
      this.resizeActive = false;
      this.removeResizeListeners();
      return;
    }
    if (!this.dragActive) return;
    try {
      if (!this.dragMoved) {
        this.toggleMinimize();
      }
    } catch {}
    this.dragActive = false;
    this.removeDragListeners();
  };

  private removeDragListeners() {
    document.removeEventListener('mousemove', this.onDocMouseMove);
    document.removeEventListener('mouseup', this.onDocMouseUp);
    document.removeEventListener('touchmove', this.onDocTouchMove as any);
    document.removeEventListener('touchend', this.onDocTouchEnd as any);
  }

  get minimizedStyle() {
    return {
      left: `${this.minimizedLeft}px`,
      bottom: `${this.minimizedBottom}px`,
      width: `${this.minimizedWidth}px`,
      height: `${this.minimizedHeight}px`,
    };
  }

  onResizeMouseDown(evt: MouseEvent, edge: string) {
    evt.preventDefault();
    evt.stopPropagation();

    this.resizeActive = true;
    this.resizeEdge = edge;
    this.resizeStartX = evt.clientX;
    this.resizeStartY = evt.clientY;
    this.resizeStartWidth = this.minimizedWidth;
    this.resizeStartHeight = this.minimizedHeight;
    this.resizeStartLeft = this.minimizedLeft;
    this.resizeStartBottom = this.minimizedBottom;

    document.addEventListener('mousemove', this.onDocMouseMove);
    document.addEventListener('mouseup', this.onDocMouseUp);
  }

  private handleResize(evt: MouseEvent) {
    const MIN_WIDTH = 120;
    const MIN_HEIGHT = 90;
    const MAX_WIDTH = 600;
    const MAX_HEIGHT = 400;
    const PADDING = 8;

    const mouseX = evt.clientX;
    const mouseY = evt.clientY;

    let newWidth = this.minimizedWidth;
    let newHeight = this.minimizedHeight;
    let newLeft = this.minimizedLeft;
    let newBottom = this.minimizedBottom;

    // Calculate the current top position
    const currentTop =
      window.innerHeight - this.minimizedBottom - this.minimizedHeight;

    // Handle horizontal resize
    if (this.resizeEdge.includes('right')) {
      const requestedWidth = mouseX - this.minimizedLeft;
      newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, requestedWidth));
      newWidth = Math.min(
        newWidth,
        window.innerWidth - this.minimizedLeft - PADDING
      );
    } else if (this.resizeEdge.includes('left')) {
      const requestedLeft = mouseX;
      const maxLeft = this.minimizedLeft + this.minimizedWidth - MIN_WIDTH;
      newLeft = Math.max(PADDING, Math.min(maxLeft, requestedLeft));
      newWidth = this.minimizedLeft + this.minimizedWidth - newLeft;
      newWidth = Math.min(MAX_WIDTH, newWidth);
    }

    // Handle vertical resize
    if (this.resizeEdge.includes('top')) {
      const requestedTop = mouseY;
      const maxTop = window.innerHeight - this.minimizedBottom - MIN_HEIGHT;
      const constrainedTop = Math.max(PADDING, Math.min(maxTop, requestedTop));
      newHeight = window.innerHeight - this.minimizedBottom - constrainedTop;
      newHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, newHeight));
      newBottom = this.minimizedBottom;
    } else if (this.resizeEdge.includes('bottom')) {
      const requestedBottom = window.innerHeight - mouseY;
      const maxBottom = window.innerHeight - currentTop - MIN_HEIGHT;
      newBottom = Math.max(PADDING, Math.min(maxBottom, requestedBottom));
      newHeight = window.innerHeight - currentTop - newBottom;
      newHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, newHeight));
    }

    if (newLeft + newWidth > window.innerWidth - PADDING) {
      newWidth = window.innerWidth - newLeft - PADDING;
    }
    if (newBottom + newHeight > window.innerHeight - PADDING) {
      newHeight = window.innerHeight - newBottom - PADDING;
    }

    this.minimizedWidth = Math.round(newWidth);
    this.minimizedHeight = Math.round(newHeight);
    this.minimizedLeft = Math.round(newLeft);
    this.minimizedBottom = Math.round(newBottom);
  }

  private removeResizeListeners() {
    document.removeEventListener('mousemove', this.onDocMouseMove);
    document.removeEventListener('mouseup', this.onDocMouseUp);
  }
}
